'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { KEYBOARD_ROWS } from '@/lib/hangulIme';
import { capLabel } from '@/lib/directIme';
import { DAKUON, GOJUON } from '@/lib/romajiKana';
import type { GeneratedKey, GeneratedLayout } from '@/lib/keyboardLayouts.generated';
import type { Candidate } from '@/lib/pinyinIme';
import type { KeyboardSpec } from '@/lib/keyboards';

/**
 * The keyboard, drawn under the box — for whichever language is in front of it.
 *
 * ── Why a picture of a keyboard, and not just the key mapping ───────────────
 * The mapping alone is invisible: press `r`, get `ㄱ` or `к` or `ρ`, and there
 * is no way to find out where `ㅎ` lives except by hunting for it. Google
 * Translate answers that with exactly this panel, and the answer is right —
 * the reader is looking at the layout while their own fingers are on the same
 * keys, so the second word is typed faster than the first. It is a lesson that
 * happens to also be clickable, which is what makes it useful on a phone.
 *
 * The Latin letter on each cap is the whole of that lesson and is not
 * decoration: it is the only thing on screen connecting `ㅁ` to the `a` under
 * the reader's finger.
 *
 * ── Four panels, because they are four different problems ───────────────────
 * `direct` and `hangul` are both a picture of the physical keyboard and share
 * every pixel of the renderer below. `romaji` is NOT — there is no mapping to
 * teach, because the reader already knows `ka` is か, so what helps is the
 * gojūon chart rather than a QWERTY diagram. `pinyin` is not a keyboard at all
 * but a candidate list: the keys are the reader's own, and the only thing this
 * panel can usefully show is which characters are one number-key away.
 *
 * ── Focus ───────────────────────────────────────────────────────────────────
 * Every key cancels its own mousedown. A button that took focus would empty
 * the textarea's selection, and the next character would compose against the
 * wrong place — or against nothing at all. The caret must never leave the box
 * while this panel is being used, so nothing in here is allowed to be focused
 * by a click. Keys stay reachable by Tab for anyone driving it from the
 * keyboard, which costs nothing because that reader is not composing with a
 * mouse anyway.
 */
export default function ScriptKeyboard({
  spec,
  language,
  layout,
  onChar,
  onSpace,
  onBackspace,
  onText,
  open,
  onOpenChange,
  pressed,
  armed,
  candidates,
  onChoose,
  dictError,
}: {
  spec: KeyboardSpec;
  /** The language's English name — "Russian", "Korean". */
  language: string;
  /** The generated layout, for `direct`. Null for the other three engines. */
  layout: GeneratedLayout | null;
  /** A character from a cap. `dead` means it arms an accent instead of typing. */
  onChar: (char: string, dead: boolean) => void;
  onSpace: () => void;
  onBackspace: () => void;
  /** Plain text from a key the language does not remap (digits, punctuation, Enter). */
  onText?: (text: string) => void;
  /** Whether the layout is drawn. False collapses it to a bar; the IME keeps running either way. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The key the reader just pressed on their own keyboard, with a counter so repeats register. */
  pressed?: { key: string; n: number } | null;
  /** The dead-key mark waiting for a letter, if any. */
  armed?: string | null;
  /** Chinese only: what the pending pinyin could become. */
  candidates?: Candidate[];
  onChoose?: (c: Candidate) => void;
  /** Chinese only: the dictionary did not arrive. */
  dictError?: string | null;
}) {
  /**
   * Shift, as a latch rather than a held key.
   *
   * A panel driven by a mouse has no way to hold a modifier down, so it works
   * the way a phone keyboard does: press it, type one character, and it lets
   * go by itself. On Korean only seven caps change under it; on Greek or
   * Russian it is every letter, which is the case that makes the latch worth
   * having rather than merely tolerable.
   */
  const [shift, setShift] = useState(false);

  /**
   * The key lit right now, the way a typing tutor lights one.
   *
   * It answers the question the panel otherwise leaves open: the reader types
   * `r`, a letter appears in the box, and nothing on the layout says WHICH key
   * did it — so the panel is a chart to be studied rather than something that
   * teaches while you type. Lighting the cap ties the finger to the letter.
   *
   * Keyed on the Latin letter rather than the character produced, because that
   * is what was actually pressed: with Shift held, `r` produces ㄲ or Р, and
   * matching on the output would light nothing at all while the cap still
   * reads ㄱ.
   *
   * 140ms — long enough to see at speed, short enough that a fast typist gets
   * one flash per key rather than a smear. A fresh press replaces the old one
   * instead of queueing, so holding a key down does not leave a trail.
   */
  const [lit, setLit] = useState<string | null>(null);
  const litTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flash = useCallback((key: string) => {
    if (litTimer.current) clearTimeout(litTimer.current);
    setLit(key);
    litTimer.current = setTimeout(() => setLit(null), 140);
  }, []);

  // The parent reports the reader's own keystrokes. `pressed.n` is what this
  // watches: the same key twice is two presses, and the key alone would not
  // change on the second.
  useEffect(() => {
    if (pressed) flash(pressed.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pressed?.n]);

  useEffect(() => () => { if (litTimer.current) clearTimeout(litTimer.current); }, []);

  /** Shared by every key: no focus stealing, no form submission, no selection. */
  const noFocus = (e: React.MouseEvent) => e.preventDefault();

  /**
   * What the panel says it is doing, in one line.
   *
   * Always names the SCRIPT rather than the language, because the surprising
   * half is what the keys now produce. "your keys type Cyrillic" is the
   * sentence somebody needs when their `r` started making `к`.
   */
  const title = `${language} keyboard`;
  const subtitle =
    spec.engine === 'pinyin' ? 'type the sound, pick the character'
      : spec.engine === 'romaji' ? 'your keys type kana'
        : `your keys type ${spec.script}`;

  const shell =
    'mt-3 rounded-xl border border-ink-200 bg-ink-50/70 dark:border-ink-700 dark:bg-ink-800/40';

  /**
   * Collapsed: a bar, not an absence.
   *
   * This used to be a ✕ that turned the whole thing OFF — panel and key
   * mapping together — so putting the layout away silently handed the reader
   * back a Latin keyboard, and the next word they typed came out as `dkssud`.
   * Hiding a diagram and changing what the keys do are two different wishes,
   * and only one of them was ever asked for by that button.
   *
   * So the bar stays behind, saying in as many words that the keys are still
   * remapped. An empty space would claim the feature had been switched off,
   * which is exactly the misunderstanding this is fixing. The whole bar is the
   * target rather than just the chevron — it is one line high, and a 14px hit
   * area on the right edge of it would be a worse version of the same problem.
   *
   * ⚠️ Chinese is the exception, and it has to be: the candidate bar is not a
   * diagram, it is the only way to choose a character. Collapsing it would
   * leave somebody typing Latin into the box with no way to convert it. So the
   * candidates ride in the collapsed bar too.
   */
  if (!open) {
    const hasCandidates = spec.engine === 'pinyin' && !!candidates?.length;
    return (
      <div className={shell}>
        <button
          type="button"
          onClick={() => onOpenChange(true)}
          onMouseDown={noFocus}
          aria-expanded={false}
          aria-label={`Show the ${language} keyboard`}
          title={`Show the ${language} keyboard`}
          className={'flex w-full items-center gap-2 rounded-xl px-3 py-1.5 text-left transition ' +
                     'hover:bg-ink-100 dark:hover:bg-ink-800'}
        >
          <KeyboardGlyph />
          <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-ink-500 dark:text-ink-400">
            {title}
            <span className="ml-2 font-normal text-ink-400 dark:text-ink-500">{subtitle}</span>
          </span>
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0 text-ink-400" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 14l5-5 5 5" />
          </svg>
        </button>
        {hasCandidates && (
          <div className="border-t border-ink-200 px-1.5 py-1.5 dark:border-ink-700">
            <CandidateBar candidates={candidates!} onChoose={onChoose} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={shell + ' p-2.5'}
      /* The panel itself must not take focus either — a click on the padding
         between two keys is still a click inside the box's workspace. */
      onMouseDown={noFocus}
    >
      <div className="mb-2 flex items-center justify-between gap-2 px-0.5">
        <span className="min-w-0 truncate text-[11px] font-medium text-ink-500 dark:text-ink-400">
          {title}
          {/* Said out loud because it is the surprising half: the panel is the
              visible part, but the reader's OWN keyboard has just been
              remapped, and nothing else on screen would tell them so. */}
          <span className="ml-2 font-normal text-ink-400 dark:text-ink-500">{subtitle}</span>
        </span>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          onMouseDown={noFocus}
          aria-expanded
          aria-label={`Hide the ${language} keyboard layout`}
          title="Hide the layout — your keys keep working the same way"
          className="shrink-0 rounded-full p-1 text-ink-400 transition hover:bg-ink-200/60
                     hover:text-ink-700 dark:hover:bg-ink-700 dark:hover:text-ink-200"
        >
          {/* A chevron, not a ✕. The cross said "close this feature" and people
              read it correctly — which was the bug, because it did rather more
              than close the panel. A chevron promises only that something folds
              away, and the bar it folds into is right there to prove it. */}
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 10l5 5 5-5" />
          </svg>
        </button>
      </div>

      {spec.note && (
        <p className="mb-2 px-0.5 text-[11px] leading-relaxed text-ink-400 dark:text-ink-500">
          {spec.note}
        </p>
      )}

      {spec.engine === 'pinyin' ? (
        <PinyinPanel candidates={candidates ?? []} onChoose={onChoose} error={dictError ?? null} />
      ) : spec.engine === 'romaji' ? (
        <KanaChart onChar={onChar} flash={flash} lit={lit} noFocus={noFocus}
                   onSpace={onSpace} onBackspace={onBackspace} />
      ) : (
        <KeyGrid
          rows={spec.engine === 'hangul' ? hangulRows() : directRows(layout)}
          shift={shift}
          setShift={setShift}
          armed={armed ?? null}
          lit={lit}
          flash={flash}
          noFocus={noFocus}
          onChar={onChar}
          onSpace={onSpace}
          onBackspace={onBackspace}
          onText={onText}
          shiftHint={spec.engine === 'hangul' ? 'Tense consonants: ㄲ ㄸ ㅃ ㅆ ㅉ' : 'Capitals and second characters'}
        />
      )}
    </div>
  );
}

function KeyboardGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0 text-ink-400" fill="none"
         stroke="currentColor" strokeWidth="1.6">
      <rect x="2" y="6" width="20" height="12" rx="2.5" />
      <path d="M6 10h.01M9.5 10h.01M13 10h.01M16.5 10h.01M7.5 13.5h9" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Korean, in the shape the generated layouts use.
 *
 * Dubeolsik lives in lib/hangulIme.ts beside the composition automaton it has
 * to agree with, so it cannot be generated into the same table as the rest.
 * Converting it here rather than duplicating it is what lets one renderer draw
 * both, and it is three lines.
 */
function hangulRows(): GeneratedKey[][] {
  return KEYBOARD_ROWS.map((row) =>
    row.map((cap) => ({ key: cap.key, base: cap.jamo, ...(cap.shifted ? { shift: cap.shifted } : {}) })),
  );
}

/** A generated layout's rows, number row first where it has one. */
function directRows(layout: GeneratedLayout | null): GeneratedKey[][] {
  if (!layout) return [];
  return layout.digits ? [layout.digits, ...layout.rows] : layout.rows;
}

const CAP_CLASS =
  'col-span-2 flex min-w-0 select-none flex-col items-center justify-center rounded-lg border ' +
  'border-ink-200 bg-white py-1.5 transition duration-75 hover:border-accent-400 hover:bg-accent-100 ' +
  'active:scale-[0.96] dark:border-ink-700 dark:bg-ink-800 dark:hover:border-accent-500 ' +
  'dark:hover:bg-accent-500/10';

const WIDE_CLASS =
  'flex select-none items-center justify-center gap-1.5 rounded-lg border border-ink-200 ' +
  'bg-white px-3 py-1.5 text-[11px] font-medium text-ink-500 transition duration-75 hover:border-ink-300 ' +
  'hover:bg-ink-100 hover:text-ink-800 active:scale-[0.98] dark:border-ink-700 dark:bg-ink-800 ' +
  'dark:text-ink-400 dark:hover:bg-ink-700 dark:hover:text-ink-100';

/**
 * What a lit key looks like: the accent, and a hair smaller, as though it had
 * been pushed down. `!` on every property because it has to beat the hover
 * colours in the base class — a key can be under the pointer and lit at the
 * same time, and the press is the thing worth seeing.
 *
 * The transition on the base classes is `duration-75`, and that number is not
 * a style choice: at Tailwind's default 150ms the colour was still fading IN
 * when the 140ms timer cleared it, so the key never reached full accent and
 * the flash looked like a grey smudge. Caught on a real capture. The fade has
 * to be comfortably shorter than the flash, or there is no flash.
 *
 * The same strong accent in BOTH themes, rather than a tint that suits each.
 * A light-theme tint was tried first (accent-200) and measured at rgb(211,
 * 227, 253) — barely a step up from the hover colour, so on white the press
 * was invisible while on black it was obvious. A pressed key has to be
 * unmistakable at speed, and that means one loud state, not a polite one.
 */
const LIT_CLASS =
  ' !border-accent-600 !bg-accent-500 !text-white !scale-[0.94] ' +
  'dark:!border-accent-400 dark:!bg-accent-500 dark:!text-white';

/** The armed dead key, which is a state rather than a flash. */
const ARMED_CLASS =
  ' !border-amber-500 !bg-amber-100 dark:!border-amber-400 dark:!bg-amber-500/20';

/**
 * The board, drawn as a real one (owner, 2026-10-15: "show the keyboards like
 * the real keyboard"): all five rows of a US ANSI board on a quarter-unit grid,
 * so every row ends flush the way a physical one does, with Tab, Caps Lock,
 * Enter, both Shifts, Backspace and the space bar where hands expect them.
 *
 * A key the language maps (ㅂ on q) shows what it types, large, with the Latin
 * letter in the top-left corner and its Shift character in the top-right —
 * which is how Korean keycaps are actually printed. A key the language leaves
 * alone (digits, most punctuation) shows its usual legends and types itself.
 * Ctrl and Alt are drawn for the shape only and do nothing.
 */
type Slot =
  | { kind: 'key'; key: string; w: number }
  | { kind: 'mod'; id: 'backspace' | 'tab' | 'caps' | 'enter' | 'shift' | 'space' | 'ctrl' | 'alt'; label: string; w: number; align?: 'left' | 'right' };

/** Widths in quarter units: 60 per row, a letter key is 4. */
const BOARD: Slot[][] = [
  [...[...'`1234567890-='].map((key) => ({ kind: 'key' as const, key, w: 4 })), { kind: 'mod', id: 'backspace', label: 'backspace', w: 8, align: 'right' }],
  [{ kind: 'mod', id: 'tab', label: 'tab', w: 6, align: 'left' }, ...[...'qwertyuiop[]'].map((key) => ({ kind: 'key' as const, key, w: 4 })), { kind: 'key', key: '\\', w: 6 }],
  [{ kind: 'mod', id: 'caps', label: 'caps lock', w: 7, align: 'left' }, ...[..."asdfghjkl;'"].map((key) => ({ kind: 'key' as const, key, w: 4 })), { kind: 'mod', id: 'enter', label: 'enter', w: 9, align: 'right' }],
  [{ kind: 'mod', id: 'shift', label: 'shift', w: 9, align: 'left' }, ...[...'zxcvbnm,./'].map((key) => ({ kind: 'key' as const, key, w: 4 })), { kind: 'mod', id: 'shift', label: 'shift', w: 11, align: 'right' }],
  [{ kind: 'mod', id: 'ctrl', label: 'ctrl', w: 6 }, { kind: 'mod', id: 'alt', label: 'alt', w: 6 }, { kind: 'mod', id: 'space', label: '', w: 36 }, { kind: 'mod', id: 'alt', label: 'alt', w: 6 }, { kind: 'mod', id: 'ctrl', label: 'ctrl', w: 6 }],
];

/** What an unmapped key types, with and without Shift (US layout). */
const US_SHIFT: Record<string, string> = {
  '`': '~', 1: '!', 2: '@', 3: '#', 4: '$', 5: '%', 6: '^', 7: '&', 8: '*', 9: '(', 0: ')', '-': '_', '=': '+',
  '[': '{', ']': '}', '\\': '|', ';': ':', "'": '"', ',': '<', '.': '>', '/': '?',
};

/** A keycap: a lighter top on a darker skirt, so it reads as something to press. */
const KEYCAP =
  'relative flex h-10 min-w-0 select-none rounded-[7px] border border-ink-300/90 border-b-[3px] border-b-ink-300 ' +
  'bg-gradient-to-b from-white to-ink-50 text-ink-900 shadow-[0_1px_1px_rgba(0,0,0,0.06)] transition duration-75 ' +
  'hover:from-accent-50 hover:to-accent-100/60 hover:border-accent-300 active:translate-y-px active:border-b ' +
  'sm:h-11 dark:border-black/50 dark:border-b-black/70 dark:from-ink-700 dark:to-ink-800 dark:text-ink-50 ' +
  'dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] dark:hover:from-ink-600 dark:hover:to-ink-700 dark:hover:border-accent-500/60';
/** Modifier caps sit a shade darker, as they do on most boards. */
const MODCAP =
  ' !from-ink-100 !to-ink-200/80 text-ink-500 dark:!from-ink-800 dark:!to-ink-900 dark:text-ink-400';
const DEAD_MOD = ' pointer-events-none opacity-60';
const LEG_SMALL = 'pointer-events-none absolute text-[9px] leading-none text-ink-400 sm:text-[10px] dark:text-ink-500';

function KeyGrid({
  rows, shift, setShift, armed, lit, flash, noFocus, onChar, onSpace, onBackspace, onText, shiftHint,
}: {
  rows: GeneratedKey[][];
  shift: boolean;
  setShift: (fn: (s: boolean) => boolean) => void;
  armed: string | null;
  lit: string | null;
  flash: (key: string) => void;
  noFocus: (e: React.MouseEvent) => void;
  onChar: (char: string, dead: boolean) => void;
  onSpace: () => void;
  onBackspace: () => void;
  onText?: (text: string) => void;
  shiftHint: string;
}) {
  /** Caps Lock: Shift that stays down until it is pressed again. */
  const [caps, setCaps] = useState(false);
  const mapped = new Map<string, GeneratedKey>();
  for (const row of rows) for (const cap of row) mapped.set(cap.key, cap);
  const up = shift !== caps;

  const press = (cap: GeneratedKey) => {
    const useShift = up && !!cap.shift;
    const char = useShift ? cap.shift! : cap.base;
    const dead = !!(cap.dead && (useShift ? cap.dead & 2 : cap.dead & 1));
    onChar(char, dead);
    flash(cap.key);
    if (shift) setShift(() => false);
  };
  const pressPlain = (key: string) => {
    if (!onText) return;
    onText(shift && US_SHIFT[key] ? US_SHIFT[key] : key);
    flash(key);
    if (shift) setShift(() => false);
  };

  const keyCap = (slot: Extract<Slot, { kind: 'key' }>, col: number) => {
    const span = { gridColumn: `${col} / span ${slot.w}` };
    const cap = mapped.get(slot.key);
    if (cap) {
      const useShift = up && !!cap.shift;
      const showing = useShift ? cap.shift! : cap.base;
      const other = useShift ? cap.base : cap.shift;
      const isDead = !!(cap.dead && (useShift ? cap.dead & 2 : cap.dead & 1));
      return (
        <button key={slot.key} type="button" onClick={() => press(cap)} onMouseDown={noFocus} style={span}
                aria-label={`${capLabel(showing)} (${slot.key})`}
                className={KEYCAP + (lit === cap.key ? LIT_CLASS : '') + (isDead && armed === showing ? ARMED_CLASS : '')}>
          <span className={LEG_SMALL + ' left-1.5 top-1 font-medium uppercase'}>{slot.key}</span>
          {other && other !== showing && <span className={LEG_SMALL + ' right-1.5 top-1 hidden sm:block'}>{capLabel(other)}</span>}
          <span className="pointer-events-none m-auto translate-y-[3px] truncate text-[15px] leading-none sm:text-[17px]">{capLabel(showing)}</span>
          {(slot.key === 'f' || slot.key === 'j') && <span aria-hidden className="pointer-events-none absolute bottom-[3px] left-1/2 h-[2px] w-2.5 -translate-x-1/2 rounded-full bg-ink-300 dark:bg-ink-500" />}
        </button>
      );
    }
    // A key this language leaves alone: its usual legends, and it types itself.
    const top = US_SHIFT[slot.key];
    return (
      <button key={slot.key} type="button" onClick={() => pressPlain(slot.key)} onMouseDown={noFocus} style={span}
              disabled={!onText} aria-label={slot.key === '\\' ? 'Backslash' : slot.key}
              className={KEYCAP + ' disabled:pointer-events-none' + (lit === slot.key ? LIT_CLASS : '')}>
        {top && <span className={LEG_SMALL + ' left-1/2 top-1 -translate-x-1/2' + (shift ? ' !text-ink-700 dark:!text-ink-200' : '')}>{top}</span>}
        <span className={'pointer-events-none m-auto translate-y-[3px] text-[13px] leading-none ' + (shift && top ? 'text-ink-400 dark:text-ink-500' : 'text-ink-600 dark:text-ink-300')}>{slot.key}</span>
      </button>
    );
  };

  const modCap = (slot: Extract<Slot, { kind: 'mod' }>, col: number, n: number) => {
    const span = { gridColumn: `${col} / span ${slot.w}` };
    // On a phone the keys with an icon (backspace, enter, shift) drop their word, and caps lock says "caps".
    const iconOnPhone = slot.id === 'backspace' || slot.id === 'enter' || slot.id === 'shift';
    const legend = (
      <span className={'pointer-events-none absolute bottom-1.5 whitespace-nowrap text-[10px] font-medium leading-none sm:text-[11px] ' +
        (iconOnPhone ? 'hidden sm:block ' : '') +
        (slot.align === 'right' ? 'right-2' : slot.align === 'left' ? 'left-2' : 'left-1/2 -translate-x-1/2')}>
        {slot.id === 'caps' ? <><span className="sm:hidden">caps</span><span className="hidden sm:inline">caps lock</span></> : slot.label}
      </span>
    );
    const key = `${slot.id}-${n}`;
    switch (slot.id) {
      case 'shift':
        return (
          <button key={key} type="button" onClick={() => setShift((s) => !s)} onMouseDown={noFocus} style={span} aria-pressed={shift} title={shiftHint}
                  className={KEYCAP + MODCAP + (shift ? ' !border-accent-500 !from-accent-100 !to-accent-200 !text-accent-800 dark:!from-accent-500/30 dark:!to-accent-500/20 dark:!text-accent-100' : '')}>
            <svg viewBox="0 0 24 24" className={'pointer-events-none absolute top-1.5 h-3.5 w-3.5 ' + (slot.align === 'right' ? 'right-2' : 'left-2')} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 4l7 7h-4v6H9v-6H5l7-7z" /></svg>
            {legend}
          </button>
        );
      case 'caps':
        return (
          <button key={key} type="button" onClick={() => setCaps((c) => !c)} onMouseDown={noFocus} style={span} aria-pressed={caps} title="Caps Lock: Shift that stays on"
                  className={KEYCAP + MODCAP}>
            <span aria-hidden className={'pointer-events-none absolute left-2 top-1.5 h-1.5 w-1.5 rounded-full ' + (caps ? 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.9)]' : 'bg-ink-300 dark:bg-ink-600')} />
            {legend}
          </button>
        );
      case 'backspace':
        return (
          <button key={key} type="button" onClick={() => { onBackspace(); flash('backspace'); }} onMouseDown={noFocus} style={span} aria-label="Backspace" title="Backspace"
                  className={KEYCAP + MODCAP + (lit === 'backspace' ? LIT_CLASS : '')}>
            <svg viewBox="0 0 24 24" className="pointer-events-none absolute right-2 top-1.5 h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 5h10a2 2 0 012 2v10a2 2 0 01-2 2H9L2.5 12 9 5z" /><path d="M12.5 9.5l5 5M17.5 9.5l-5 5" />
            </svg>
            {legend}
          </button>
        );
      case 'enter':
        return (
          <button key={key} type="button" onClick={() => { onText?.('\n'); flash('enter'); }} onMouseDown={noFocus} style={span} aria-label="Enter (new line)" title="New line"
                  disabled={!onText} className={KEYCAP + MODCAP + ' disabled:pointer-events-none' + (lit === 'enter' ? LIT_CLASS : '')}>
            <svg viewBox="0 0 24 24" className="pointer-events-none absolute right-2 top-1.5 h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 5v7H6M10 8l-4 4 4 4" /></svg>
            {legend}
          </button>
        );
      case 'space':
        return (
          <button key={key} type="button" onClick={() => { onSpace(); flash('space'); }} onMouseDown={noFocus} style={span} aria-label="Space"
                  className={KEYCAP + (lit === 'space' ? LIT_CLASS : '')}>
            <span className="pointer-events-none m-auto text-[10px] font-medium tracking-wide text-ink-400 dark:text-ink-500">space</span>
          </button>
        );
      default:
        // Tab, Ctrl and Alt: there for the shape of the board, not for use.
        return <span key={key} aria-hidden style={span} className={KEYCAP + MODCAP + DEAD_MOD}>{legend}</span>;
    }
  };

  return (
    // The deck the keys sit in: a darker plate with a soft inner shadow.
    <div className="rounded-xl bg-gradient-to-b from-ink-200/80 to-ink-300/60 p-1.5 shadow-[inset_0_1px_3px_rgba(0,0,0,0.12)] sm:p-2 dark:from-black/50 dark:to-black/30 dark:shadow-[inset_0_1px_4px_rgba(0,0,0,0.6)]">
      <div className="flex flex-col gap-[3px] sm:gap-1">
        {BOARD.map((row, ri) => {
          let col = 1;
          return (
            <div key={ri} className="grid gap-[3px] sm:gap-1" style={{ gridTemplateColumns: 'repeat(60, minmax(0, 1fr))' }}>
              {row.map((slot, si) => {
                const at = col;
                col += slot.w;
                return slot.kind === 'key' ? keyCap(slot, at) : modCap(slot, at, si);
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The gojūon, for Japanese.
 *
 * A chart rather than a keyboard diagram, for the reason the header comment
 * gives: romaji has no mapping to learn. What this teaches instead is the
 * SPELLING — each cell shows the kana with the letters that produce it, so
 * somebody unsure whether it is `si` or `shi` can see that both are on し.
 */
function KanaChart({
  onChar, flash, lit, noFocus, onSpace, onBackspace,
}: {
  onChar: (char: string, dead: boolean) => void;
  flash: (key: string) => void;
  lit: string | null;
  noFocus: (e: React.MouseEvent) => void;
  onSpace: () => void;
  onBackspace: () => void;
}) {
  const cell = (c: { kana: string; romaji: string }) =>
    c.kana ? (
      <button
        key={c.romaji}
        type="button"
        // The kana goes in WHOLE, not as romaji replayed through the engine.
        // A tap is a choice of kana; running it back through the spelling
        // rules would make ん out of a tapped な the moment the next key was a
        // consonant, which is a rule about typing and not about tapping.
        onClick={() => { onChar(c.kana, false); flash(c.romaji); }}
        onMouseDown={noFocus}
        aria-label={`${c.kana} (${c.romaji})`}
        className={CAP_CLASS + ' col-span-1' + (lit === c.romaji ? LIT_CLASS : '')}
      >
        <span className="text-[15px] leading-none text-ink-900 dark:text-ink-50">{c.kana}</span>
        <span className="mt-0.5 text-[9px] leading-none text-ink-400 dark:text-ink-500">{c.romaji}</span>
      </button>
    ) : (
      <span key={`gap-${c.romaji}${Math.random()}`} />
    );

  /*
   * FIVE ROWS, FILLED DOWN THE COLUMNS — the gojuon as it is printed.
   *
   * The data is [consonant row][vowel], so laying it out row-major gives ten
   * rows of five: correct, and so tall it filled the whole pane and pushed the
   * translation off the screen. `grid-flow-col` over five rows transposes it
   * without touching the data, which is both half the height and the shape
   * every Japanese chart uses — あかさたな across the top, あいうえお down the side.
   *
   * Row-major is kept below `sm`, where ten columns would not fit anyway.
   */
  const chartClass =
    'grid grid-cols-5 gap-1 sm:grid-cols-none sm:grid-rows-5 sm:grid-flow-col';

  return (
    <div className="flex flex-col gap-1.5">
      <div className={chartClass}>{GOJUON.flat().map(cell)}</div>
      <div className={chartClass}>{DAKUON.flat().map(cell)}</div>
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={() => { onSpace(); flash('space'); }}
          onMouseDown={noFocus}
          aria-label="Space"
          className={WIDE_CLASS + ' flex-1' + (lit === 'space' ? LIT_CLASS : '')}
        >
          space
        </button>
        <button
          type="button"
          onClick={() => { onBackspace(); flash('backspace'); }}
          onMouseDown={noFocus}
          aria-label="Backspace"
          className={WIDE_CLASS + (lit === 'backspace' ? LIT_CLASS : '')}
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor"
               strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 5h10a2 2 0 012 2v10a2 2 0 01-2 2H9L2.5 12 9 5z" />
            <path d="M12.5 9.5l5 5M17.5 9.5l-5 5" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/**
 * The candidate list, and the instructions for a control nobody can guess.
 *
 * Every other panel here is self-explanatory once seen. This one is not: the
 * whole interaction is "type Latin, then press a number or the space bar",
 * and a row of characters explains none of it. So the row carries its own
 * numbers and the empty state says what to do.
 */
function PinyinPanel({
  candidates, onChoose, error,
}: {
  candidates: Candidate[];
  onChoose?: (c: Candidate) => void;
  error: string | null;
}) {
  if (error) {
    return (
      <p className="px-1 py-3 text-center text-[11px] text-red-600 dark:text-red-400">{error}</p>
    );
  }
  if (!candidates.length) {
    return (
      <p className="px-1 py-3 text-center text-[11px] leading-relaxed text-ink-400 dark:text-ink-500">
        Type the pinyin — <span className="font-mono text-ink-500 dark:text-ink-400">nihao</span>,{' '}
        <span className="font-mono text-ink-500 dark:text-ink-400">zhongguo</span> — and the
        characters appear here. Press a number to pick one, or the space bar for the first.
      </p>
    );
  }
  return <CandidateBar candidates={candidates} onChoose={onChoose} />;
}

function CandidateBar({
  candidates, onChoose,
}: {
  candidates: Candidate[];
  onChoose?: (c: Candidate) => void;
}) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-0.5">
      {candidates.map((c, i) => (
        <button
          key={`${c.text}-${i}`}
          type="button"
          onClick={() => onChoose?.(c)}
          onMouseDown={(e) => e.preventDefault()}
          title={`Press ${i + 1}`}
          className={
            'flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 transition ' +
            'border-ink-200 bg-white hover:border-accent-400 hover:bg-accent-100 ' +
            'dark:border-ink-700 dark:bg-ink-800 dark:hover:border-accent-500 ' +
            'dark:hover:bg-accent-500/10 ' +
            // The first one is what the space bar takes, so it is the one that
            // has to look chosen already.
            (i === 0 ? '!border-accent-500 bg-accent-50 dark:!border-accent-500' : '')
          }
        >
          <span className="text-[10px] leading-none text-ink-400 dark:text-ink-500">{i + 1}</span>
          <span className="text-[15px] leading-none text-ink-900 dark:text-ink-50">{c.text}</span>
        </button>
      ))}
    </div>
  );
}
